import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { CreateRepoInput } from '../../types';

const useStyles = makeStyles(theme => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  groupSection: {
    marginTop: theme.spacing(2),
  },
  groupLabel: {
    marginBottom: theme.spacing(1),
  },
  error: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
}));

interface GitRepoContributionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateRepoInput) => Promise<void>;
  userGroups: string[];
  userRef?: string;
  initialUrl?: string;
  preSelectMyself?: boolean;
  initialGroups?: string[];
}

/**
 * Validates that a string is a valid GitHub repository URL.
 */
function isValidGitHubRepoUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const isGitHub = parsed.hostname === 'github.com' ||
                     parsed.hostname === 'www.github.com';
    const pathParts = parsed.pathname.split('/').filter(Boolean);
    const hasValidPath = pathParts.length >= 2;
    return isGitHub && hasValidPath;
  } catch {
    return false;
  }
}

/**
 * Parses a GitHub URL to extract owner and repo name.
 */
function parseGitHubUrl(url: string): { owner: string; name: string } | null {
  try {
    const parsed = new URL(url);
    const pathParts = parsed.pathname.split('/').filter(Boolean);
    if (pathParts.length < 2) {
      return null;
    }
    let name = pathParts[1];
    if (name.endsWith('.git')) {
      name = name.slice(0, -4);
    }
    return {
      owner: pathParts[0],
      name: name,
    };
  } catch {
    return null;
  }
}

export const GitRepoContributionDialog = ({
  open,
  onClose,
  onSubmit,
  userGroups,
  userRef,
  initialUrl,
  preSelectMyself,
  initialGroups,
}: GitRepoContributionDialogProps) => {
  const classes = useStyles();
  const [repoUrl, setRepoUrl] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parsedRepo, setParsedRepo] = useState<{ owner: string; name: string } | null>(null);

  useEffect(() => {
    if (open) {
      if (initialUrl) {
        setRepoUrl(initialUrl);
        setParsedRepo(parseGitHubUrl(initialUrl));
      }
      // Priority: initialGroups (editing) > preSelectMyself (drag-drop)
      if (initialGroups && initialGroups.length > 0) {
        setSelectedGroups(initialGroups);
      } else if (preSelectMyself && userRef) {
        setSelectedGroups([userRef]);
      }
    }
  }, [open, initialUrl, preSelectMyself, userRef, initialGroups]);

  useEffect(() => {
    if (repoUrl) {
      setParsedRepo(parseGitHubUrl(repoUrl));
    } else {
      setParsedRepo(null);
    }
  }, [repoUrl]);

  const resetForm = useCallback(() => {
    setRepoUrl('');
    setSelectedGroups([]);
    setError(null);
    setParsedRepo(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleGroupToggle = useCallback((groupRef: string) => {
    setSelectedGroups(prev =>
      prev.includes(groupRef)
        ? prev.filter(g => g !== groupRef)
        : [...prev, groupRef],
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!repoUrl.trim()) {
      setError('Repository URL is required');
      return;
    }
    if (!isValidGitHubRepoUrl(repoUrl)) {
      setError('Please enter a valid GitHub repository URL (e.g., https://github.com/owner/repo)');
      return;
    }
    if (selectedGroups.length === 0) {
      setError('Select at least one option (Myself or a group)');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await onSubmit({
        repoUrl: repoUrl.trim(),
        groupRefs: selectedGroups,
      });
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add repository');
    } finally {
      setSubmitting(false);
    }
  }, [repoUrl, selectedGroups, onSubmit, handleClose]);

  const formatGroupName = (groupRef: string) => {
    const parts = groupRef.split('/');
    const name = parts[parts.length - 1];
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add GitHub Repository</DialogTitle>
      <DialogContent>
        <TextField
          className={classes.field}
          label="GitHub Repository URL"
          value={repoUrl}
          onChange={e => setRepoUrl(e.target.value)}
          variant="outlined"
          fullWidth
          required
          placeholder="https://github.com/owner/repo"
          error={Boolean(error && !repoUrl)}
        />
        {parsedRepo && (
          <Typography className={classes.helperText}>
            Repository: {parsedRepo.owner}/{parsedRepo.name}
          </Typography>
        )}

        <FormControl component="fieldset" className={classes.groupSection}>
          <FormLabel component="legend" className={classes.groupLabel}>
            Add for *
          </FormLabel>
          <FormGroup>
            {userRef && (
              <FormControlLabel
                key="myself"
                control={
                  <Checkbox
                    checked={selectedGroups.includes(userRef)}
                    onChange={() => handleGroupToggle(userRef)}
                    color="primary"
                  />
                }
                label="Myself (personal)"
              />
            )}
            {userGroups.map(groupRef => (
              <FormControlLabel
                key={groupRef}
                control={
                  <Checkbox
                    checked={selectedGroups.includes(groupRef)}
                    onChange={() => handleGroupToggle(groupRef)}
                    color="primary"
                  />
                }
                label={formatGroupName(groupRef)}
              />
            ))}
          </FormGroup>
          {!userRef && userGroups.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              You are not a member of any groups.
            </Typography>
          )}
        </FormControl>

        {error && (
          <Typography className={classes.error} variant="body2">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={submitting || (!userRef && userGroups.length === 0)}
        >
          {submitting ? 'Adding...' : 'Add Repository'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
